import { isFunction, isObject, pick } from 'lodash';

interface IJsonStringResult {
    json: string;
    success: boolean;
}

export function tryParseToJsonString(item: any): IJsonStringResult {
    if (!item) {
        return { json: '', success: true };
    }

    try {
        return {
            json: JSON.stringify(item, replaceWithoutCircularProps),
            success: true
        };
    } catch (e) {
        return { json: item.toString(), success: false };
    }
}

const CircularProps = [
    'rootStore',
    'flowRevision',
    'globalContext',
    '__parent',
    'window',
    '__pubSubEvents',
    'self'
];

export const replaceWithoutCircularProps = (fieldName, value) => {
    if (CircularProps.indexOf(fieldName) !== -1 || isFunction(value)) {
        return undefined;
    }

    if (isObject(value) && !Array.isArray(value)) {
        return { ...value };
    }

    return value;
};

// safely handles circular references
export const safeStringify = (
    obj,
    {
        shouldRemoveIframes = false,
        propsToPick = [],
        cacheThreshold = 5
    }: {
        shouldRemoveIframes?: boolean;
        propsToPick?: Array<string>;
        cacheThreshold?: number;
    }
) => {
    const indent = 4;
    let objectToStringify = {};
    if (propsToPick.length > 0) {
        Object.assign(objectToStringify, pick(obj, propsToPick));
    } else {
        Object.assign(objectToStringify, obj);
    }
    // we needed this since having iframe issues when logging.
    if (shouldRemoveIframes) {
        document.querySelectorAll('iframe').forEach(iframe => iframe.remove());
    }
    const cache = {};
    const safeObjectStringResult = JSON.stringify(
        objectToStringify,
        (key, value) => {
            if (typeof value === 'object' && value !== null) {
                // If we reached to a certain threshold we mark this prop as circular prop and stop the loop.
                if (cache[value] === cacheThreshold) {
                    return;
                }

                // Track the amount of instances of this property
                cache[value] = cache[value] ? cache[value] + 1 : 1;
            }
            return value;
        },
        indent
    );

    return safeObjectStringResult;
};

export const convertToCleanJsonString = item =>
    JSON.stringify(item, replaceWithoutCircularProps, 2);

export const convertToCleanJson = item =>
    JSON.parse(JSON.stringify(item, replaceWithoutCircularProps));

export const jsonClone = item => JSON.parse(JSON.stringify(item));

export function findDifferentByKeys<T extends string[]>({
    item1,
    item2,
    keys
}: {
    item1: Partial<Record<T[number], any>>;
    item2: Partial<Record<T[number], any>>;
    keys: T;
}): Partial<Record<T[number], boolean>> {
    const safeItem1 = item1 || {};
    const safeItem2 = item2 || {};

    return keys
        .filter(key => safeItem1[key] !== safeItem2[key])
        .reduce((result, key) => {
            result[key] = true;

            return result;
        }, {});
}
